import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const STACKFORDEVS_AUTH_URL = process.env.REACT_APP_STACKFORDEVS_AUTH_URL || 'https://auth.stackfordevs.com/v1';
const TENANT_ID = process.env.REACT_APP_TENANT_ID;
const PROJECT_ID = process.env.REACT_APP_PROJECT_ID;
const API_KEY = process.env.REACT_APP_API_KEY;
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

const Login = ({ setUser, defaultTab = 'login' }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    company_name: '',
  });

  const getStackForDevsHeaders = () => ({
    'Content-Type': 'application/json',
    'x-tenant-id': TENANT_ID,
    'x-project-id': PROJECT_ID,
    'x-api-key': API_KEY,
    'x-secret-key': SECRET_KEY,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${STACKFORDEVS_AUTH_URL}/login`,
        loginData,
        { headers: getStackForDevsHeaders() }
      );
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success('Welcome back!');
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${STACKFORDEVS_AUTH_URL}/register`,
        registerData,
        { headers: getStackForDevsHeaders() }
      );
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success('Account created successfully!');
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Hero image */}
      <div className="hidden lg:flex lg:w-1/2 login-hero relative">
        <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center">
          <div className="text-center text-white p-8">
            <h1 className="font-heading text-5xl mb-4">PayForeman.ai</h1>
            <p className="text-xl text-slate-200">AI-Powered Collections for Subcontractors</p>
          </div>
        </div>
      </div>

      {/* Right side - Login/Register form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 rounded-none shadow-none">
          <CardHeader>
            <CardTitle className="font-heading text-3xl">Get Started</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={defaultTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 rounded-none mb-8">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="rounded-sm border-2 border-slate-200 focus:border-slate-900 h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="rounded-sm border-2 border-slate-200 focus:border-slate-900 h-12"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit-button"
                    disabled={isLoading}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none h-12 btn-primary"
                  >
                    {isLoading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name-input"
                      placeholder="John Doe"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      className="rounded-sm border-2 border-slate-200 focus:border-slate-900 h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-company">Company Name</Label>
                    <Input
                      id="register-company"
                      data-testid="register-company-input"
                      placeholder="ABC Construction"
                      value={registerData.company_name}
                      onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                      className="rounded-sm border-2 border-slate-200 focus:border-slate-900 h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="rounded-sm border-2 border-slate-200 focus:border-slate-900 h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className="rounded-sm border-2 border-slate-200 focus:border-slate-900 h-12"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="register-submit-button"
                    disabled={isLoading}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none h-12 btn-primary"
                  >
                    {isLoading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;